<template>
  <v-treeview
    :items="computedItems"
    v-model="selectedItems"
    activatable
    dense
    hoverable
    :selectable="Boolean(selectedItems.length)"
    selected-color="primary"
    multiple-active
    selection-type="independent"
    :key="`treeview-${treeviewKey}`"
    :open.sync="open"
    :search.sync="searchText"
    :active.sync="selectedItems"
    return-object
  >
    <template #prepend="{ item }">
      <v-icon v-if="item._type === 'folder'"> mdi-folder </v-icon>
      <v-icon v-else> mdi-file-document-outline </v-icon>
    </template>

    <template #label="{ item }">
      <v-list-item dense class="pl-0" @dblclick="navigateTo(item)">
        <v-list-item-content>
          <div>
            <v-list-item-title @click.stop="navigateTo(item)" class="case-link">
              {{ item.name }}
            </v-list-item-title>
          </div>
          <template v-if="item._type !== 'folder'">
            <v-list-item-subtitle>{{ item.notes }}</v-list-item-subtitle>
            <v-list-item-subtitle class="text-caption"
              >Last modified: {{ formatDate(item.updated) }}
            </v-list-item-subtitle>
          </template>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template #append="{ item }">
      <tr>
        <template v-if="item._type === 'case'">
          <td
            class="px-2 text-center job-indicator"
            v-for="calculation in calculations"
            :key="`calc-${item.id}-${calculation.type}`"
          >
            <JobIndicator
              :key="calculation.key"
              :status="getJobStatus(item.id, calculation.type)"
              :progress="getJobProgress(item.id, calculation.type)"
              :error="getJobError(item.id, calculation.type)"
              @click="handleClickJob(item.id, calculation.type)"
            />
            <v-spacer />
            <v-tooltip bottom :disabled="!calculation.name">
              <template #activator="{ on }">
                <a
                  :href="calculation.wikiUrl"
                  target="_blank"
                  class="table-header-link text-caption"
                  v-on="on"
                >
                  {{ calculation.shortName }}
                </a>
              </template>
              <span>{{ calculation.name }}</span>
            </v-tooltip>
          </td>
        </template>
        <td>
          <MenuMore offset-y left @open="selectItem(item)">
            <v-list v-if="!item.locked" dense>
              <v-list-item
                v-for="menuItem in menuMoreItems(item)"
                :key="`${item.id}-menu-${item._type}-${menuItem.actionName}`"
                @click.stop="
                  $emit(
                    item._type === 'case' ? 'case-menu-click' : 'folder-menu-click',
                    menuItem.actionName,
                    item
                  )
                "
              >
                <v-list-item-icon>
                  <v-icon>{{ menuItem.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ menuItem.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </MenuMore>
        </td>
      </tr>
    </template>
  </v-treeview>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions } from "vuex";
import { makeFindMixin } from "feathers-vuex";

export default {
  name: "FolderTree",
  components: {
    JobIndicator: () => import("@/components/JobIndicator.vue"),
    MenuMore: () => import("@/components/MenuMore.vue"),
  },
  mixins: [makeFindMixin({ service: "jobs", watch: true })],
  props: {
    items: { type: Array, required: false, default: () => [] },
    calculations: { type: Array, required: false, default: () => [] },
    selected: { type: Array, required: false, default: () => [] },
    search: { type: String, required: false, default: "" },
    sortBy: {
      type: String,
      required: false,
      default: null,
      validator: (v) => ["updated", "name"].includes(v),
    },
  },
  data() {
    return {
      value: [],
      treeviewKey: 0,
      open: [],
      caseMenu: [
        { text: "Open case", icon: "mdi-folder-open-outline", actionName: "open" },
        { text: "Open in new tab", icon: "mdi-open-in-new", actionName: "openInNew" },
        { text: "Get link", icon: "mdi-link", actionName: "getLink" },
        { text: "Move to...", icon: "mdi-folder-move-outline", actionName: "move" },
        { text: "Rename case", icon: "mdi-pencil-outline", actionName: "edit" },
        {
          text: "Duplicate case",
          icon: "mdi-file-document-multiple-outline",
          actionName: "duplicate",
        },
        { text: "Remodel case", icon: "mdi-content-copy", actionName: "remodel" },
        { text: "Delete", icon: "mdi-trash-can-outline", actionName: "delete" },
      ],
      folderMenu: [
        { text: "Open in new tab", icon: "mdi-open-in-new", actionName: "openInNew" },
        { text: "Get link", icon: "mdi-link", actionName: "getLink" },
        { text: "Move to...", icon: "mdi-folder-move-outline", actionName: "move" },
        { text: "Rename", icon: "mdi-pencil-outline", actionName: "edit" },
        { text: "Duplicate", icon: "mdi-folder-multiple-outline", actionName: "duplicate" },
        { text: "Create case...", icon: "mdi-file-plus-outline", actionName: "createCase" },
        { text: "Delete", icon: "mdi-trash-can-outline", actionName: "delete" },
      ],
      refreshJobs: null,
    };
  },
  computed: {
    searchText: {
      get() {
        return this.search;
      },
      set(v) {
        this.$emit("update:search", v);
      },
    },
    computedItems() {
      const itemsCopy = this.items;
      const sorted = this.sortTree(itemsCopy);
      return sorted;
    },
    selectedItems: {
      get() {
        return this.selected;
      },
      set(v) {
        this.$emit("update:selected", v);
      },
    },
    computedCases() {
      // eslint-disable-next-line no-underscore-dangle
      return this.items.filter((e) => e._type === "case");
    },
    computedCasesId() {
      return this.computedCases.map((e) => e.id);
    },
    // Gets all jobs in from the cases in the current folder
    jobsParams() {
      return {
        query: {
          case_id: {
            $in: this.computedCasesId,
          },
        },
      };
    },
    hasRunningJobs() {
      return !this.jobs.every((e) => e.status === "ERROR" || e.status === "COMPLETE");
    },
  },
  watch: {
    sortBy() {
      this.refreshTree();
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    this.loadJobs();
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {
    clearTimeout(this.refreshJobs);
  },
  destroyed() {},
  methods: {
    ...mapActions({
      getDirectory: "directories/get",
      findJob: "jobs/find",
      createJob: "jobs/create",
      removeJob: "jobs/remove",
    }),
    formatDate(date) {
      return date ? DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_SHORT) : "";
    },
    selectItem(item) {
      this.$emit("update:selected", [item]);
    },
    navigateTo(item) {
      // eslint-disable-next-line no-underscore-dangle
      if (item._type === "folder") {
        this.$router.push({ name: "Folders", params: { folderId: item.id } });
      } else {
        this.$router.push({ path: `/case/${item.id}/input#Settings` });
      }
    },
    sortTree(tree) {
      tree.sort((a, b) => {
        // eslint-disable-next-line no-underscore-dangle
        const compareType = a._type.localeCompare(b._type) * -1; // -1 to put 'folder' before 'cases'
        const compareUpdated = new Date(b.updated) - new Date(a.updated);
        const compareName = a.name.localeCompare(b.name);
        if (this.sortBy === "name") {
          return compareType || compareName || compareUpdated;
        }
        return compareType || compareUpdated || compareName;
      });
      tree.forEach((node) => {
        if (node.children) {
          this.sortTree(node.children);
        }
      });
      return tree;
    },
    refreshTree() {
      const { open } = this;
      this.treeviewKey += 1;
      this.open = open;
    },
    menuMoreItems(item) {
      // eslint-disable-next-line no-underscore-dangle
      const type = item._type;
      if (type === "folder") {
        return this.folderMenu;
      }
      if (type === "case") {
        const baseMenu = JSON.parse(JSON.stringify(this.caseMenu));
        // Add download outputs btn
        const jobs = Object.values(item.jobs);
        if (jobs.some((v) => v.status === "COMPLETE" || v.status === "COMPLETE_WITH_WARNINGS")) {
          baseMenu.splice(baseMenu.length - 1, 0, {
            text: "Download outputs",
            icon: "mdi-download",
            actionName: "downloadOutputs",
          });
        }
        return baseMenu;
      }
      return [];
    },
    getJob(caseId, jobType) {
      const job = this.jobs.find((e) => e.type === jobType && e.case_id === caseId);
      return job || null;
    },
    reRunJob(job) {
      return this.removeJob(job.id).then((_) => {
        this.createJob({
          case_id: job.case_id,
          status: "NOT_STARTED",
          type: job.type,
        });
      });
    },
    handleClickJob(caseId, jobType) {
      const job = this.getJob(caseId, jobType);
      if (!job) {
        this.createJob({
          case_id: caseId,
          status: "NOT_STARTED",
          type: jobType,
        }).then(() => {
          this.reloadJobs = setTimeout(this.loadJobs, 10 * 1000);
        });
      } else {
        switch (job.status) {
          case "ERROR":
            this.reRunJob(job);
            break;
          case "NOT_STARTED":
          case "IN_PROGRESS":
            this.removeJob(job.id);
            break;
          case "COMPLETE":
            this.reRunDialog = true;
            break;
          default:
        }
      }
    },
    getJobStatus(caseId, type) {
      const job = this.getJob(caseId, type);
      return job ? job.status : "NO_JOB";
    },
    getJobProgress(caseId, type) {
      const job = this.getJob(caseId, type);
      return job ? job.progress : 0;
    },
    getJobError(caseId, type) {
      const job = this.getJob(caseId, type);
      return job ? job.error : "";
    },
    loadJobs() {
      return this.$store
        .dispatch("jobs/find", {
          paginate: false,
          query: { case_id: { $in: this.computedCasesId } },
        })
        .then((_) => {
          if (this.hasRunningJobs) {
            this.refreshJobs = setTimeout(this.loadJobs, 15 * 1000);
          }
        });
    },
  },
};
</script>

<style>
td {
  vertical-align: middle;
}
.job-indicator {
  width: 60px;
}
.v-treeview-node.v-treeview-node--leaf.v-treeview-node--click {
  border-bottom: 1px solid rgba(128, 128, 128, 0.2) !important;
}
.table-header-link {
  color: rgba(0, 0, 0, 0.6) !important;
  text-decoration: none;
}
.table-header-link:hover {
  color: rgba(0, 0, 0, 0.87) !important;
}
.case-link {
  display: inline-block;
}
.case-link:hover {
  color: rgba(0, 0, 0, 0.87) !important;
  text-decoration: underline;
  cursor: pointer;
}
</style>
